// Programación: cuándo y dónde se juega cada partido.
//
// Hechos vs. derivados: el horario de un partido es un hecho (lo decide el
// organizador, a mano o aceptando lo que propone el asistente de acá). Los
// choques no se guardan: se calculan cada vez y se le avisan.

import { diaCorto } from './fechas';
import { bien, fechaValida, mal, type Resultado } from './validar';
import type { Restriccion } from './opciones';

export const CANCHAS_MAX = 20;

export interface Horario {
  dia: string | null; // YYYY-MM-DD
  hora: string | null; // HH:MM
  cancha: number | null;
}

export const SIN_HORARIO: Horario = { dia: null, hora: null, cancha: null };

const HORA = /^([01]\d|2[0-3]):[0-5]\d$/;

export const aMinutos = (hora: string): number => Number(hora.slice(0, 2)) * 60 + Number(hora.slice(3, 5));
export const aHora = (minutos: number): string =>
  `${String(Math.floor(minutos / 60)).padStart(2, '0')}:${String(minutos % 60).padStart(2, '0')}`;

/**
 * Un horario bien formado para ese torneo. Todo vacío vale (es "sacarle el
 * horario"); si no, día y hora van juntos y la cancha es opcional (a veces se
 * sabe la hora antes que la cancha).
 */
export function validarHorario(
  cuerpo: unknown,
  torneo: { fecha_desde: string; fecha_hasta: string; canchas: number | null },
): Resultado<Horario> {
  const c = typeof cuerpo === 'object' && cuerpo !== null ? (cuerpo as Record<string, unknown>) : {};
  const dia = c.dia ?? null;
  const hora = c.hora ?? null;
  const cancha = c.cancha ?? null;

  if (dia === null && hora === null && cancha === null) return bien(SIN_HORARIO);
  if (dia === null || hora === null) return mal('El día y la hora van juntos.');
  if (!fechaValida(dia)) return mal('Ese día no existe.');
  if (dia < torneo.fecha_desde || dia > torneo.fecha_hasta) return mal('Ese día está fuera de las fechas del torneo.');
  if (typeof hora !== 'string' || !HORA.test(hora)) return mal('La hora va como 18:30.');
  if (cancha !== null) {
    const tope = torneo.canchas ?? CANCHAS_MAX;
    if (typeof cancha !== 'number' || !Number.isInteger(cancha) || cancha < 1 || cancha > tope) {
      return mal(`La cancha va de 1 a ${tope}.`);
    }
  }
  return bien({ dia, hora, cancha });
}

/** "Sáb 18 · 18:00 · Cancha 2", o "" si no tiene horario. */
export function textoDeHorario(h: Horario, conDia = true): string {
  if (!h.dia || !h.hora) return h.cancha === null ? '' : `Cancha ${h.cancha}`;
  return [conDia ? diaCorto(h.dia) : null, h.hora, h.cancha === null ? null : `Cancha ${h.cancha}`].filter(Boolean).join(' · ');
}

// ── Choques ───────────────────────────────────────────────────────────────

export interface PartidoProgramado extends Horario {
  id: number;
  /** Las parejas que se saben (en el cuadro puede no saberse todavía). */
  parejas: number[];
  /**
   * Los nombres de sus jugadores, normalizados. Para avisar cuando la misma
   * persona juega en dos categorías a la vez (6ta y Suma 11, por ejemplo): son
   * dos parejas distintas, pero un solo cuerpo.
   */
  jugadores?: string[];
  jugado: boolean;
}

export interface Choque {
  tipo: 'cancha' | 'pareja' | 'jugador';
  partidos: [number, number];
  /** Solo para tipo 'pareja': la que está en los dos. */
  pareja?: number;
  /** Solo para tipo 'jugador': el nombre (normalizado) que aparece en los dos. */
  jugador?: string;
}

/**
 * Dos partidos en la misma cancha a la misma hora, una pareja en dos partidos
 * a la misma hora, o un jugador con el mismo nombre en dos partidos a la misma
 * hora (en parejas distintas: juega dos categorías). Solo entre partidos que
 * todavía no se jugaron: lo que ya pasó no es un problema a resolver.
 *
 * El de jugador es por NOMBRE, así que puede ser un homónimo: por eso es un
 * aviso para que el organizador mire, no un error.
 */
export function choques(partidos: readonly PartidoProgramado[]): Choque[] {
  const salida: Choque[] = [];
  const pendientes = partidos.filter((p) => !p.jugado && p.dia && p.hora);
  for (let i = 0; i < pendientes.length; i++) {
    for (let j = i + 1; j < pendientes.length; j++) {
      const a = pendientes[i]!;
      const b = pendientes[j]!;
      if (a.dia !== b.dia || a.hora !== b.hora) continue;
      if (a.cancha !== null && a.cancha === b.cancha) salida.push({ tipo: 'cancha', partidos: [a.id, b.id] });
      const repetida = a.parejas.find((x) => b.parejas.includes(x));
      if (repetida !== undefined) {
        salida.push({ tipo: 'pareja', partidos: [a.id, b.id], pareja: repetida });
        continue; // ya está dicho; no repetirlo como "jugador"
      }
      const mismo = (a.jugadores ?? []).find((x) => (b.jugadores ?? []).includes(x));
      if (mismo !== undefined) salida.push({ tipo: 'jugador', partidos: [a.id, b.id], jugador: mismo });
    }
  }
  return salida;
}

// ── El asistente: repartir horarios ───────────────────────────────────────

export interface PartidoARepartir {
  id: number;
  parejas: number[];
  /** Nombres normalizados de sus jugadores: nadie juega dos partidos a la vez, aunque sea en otra categoría. */
  jugadores?: string[];
  /** Partidos que tienen que ir ANTES (los de la ronda anterior del cuadro, o los del grupo). */
  despuesDe: number[];
}

export interface OpcionesDeReparto {
  dia: string;
  desde: string; // HH:MM
  /** Hasta qué hora puede EMPEZAR un partido. null = sin tope. */
  hasta: string | null;
  /** Cada cuántos minutos arranca un turno (la duración que se le da a un partido). */
  cadaMinutos: number;
  canchas: number[];
}

export interface Reparto {
  asignados: { id: number; dia: string; hora: string; cancha: number }[];
  /** Los que no entraron antes de `hasta`. */
  sinLugar: number[];
}

export interface JornadaDeReparto {
  dia: string;
  desde: string;
  /** Hora de inicio del último turno. Si es menor que desde, cae al día siguiente. */
  hasta: string | null;
}

export const diaSiguiente = (dia: string) => new Date(Date.parse(`${dia}T00:00:00Z`) + 86_400_000).toISOString().slice(0, 10);
const minutoAbsoluto = (dia: string, hora: string) => Date.parse(`${dia}T00:00:00Z`) / 60_000 + aMinutos(hora);

export interface BloqueoDisponibilidad { parejas: number[]; jugadores: string[]; restricciones: Restriccion[] }
export interface CondicionesProgramacion {
  descansoMinutos?: number;
  bloqueos?: readonly BloqueoDisponibilidad[];
}
const comparten = (a: { parejas: number[]; jugadores?: string[] }, b: { parejas: number[]; jugadores?: string[] }) =>
  a.parejas.some(id => b.parejas.includes(id)) || (a.jugadores ?? []).some(j => b.jugadores?.includes(j));

/** Comprueba el intervalo completo, incluida la madrugada, y descansos a ambos lados. */
export function problemaDeCondiciones(
  p: PartidoARepartir, dia: string, hora: string, otros: readonly PartidoProgramado[], duracion: number, condiciones: CondicionesProgramacion,
): string | null {
  const inicio = minutoAbsoluto(dia, hora);
  for (const b of condiciones.bloqueos ?? []) {
    if (!comparten(p, b)) continue;
    if (b.restricciones.some(r => inicio < minutoAbsoluto(r.dia, r.hasta) && inicio + duracion > minutoAbsoluto(r.dia, r.desde))) return 'Hay una pareja que no puede jugar en ese horario.';
  }
  const descanso = condiciones.descansoMinutos ?? 0;
  if (descanso > 0) for (const otro of otros) {
    if (otro.id === p.id || !otro.dia || !otro.hora || !comparten(p, otro)) continue;
    const otroInicio = minutoAbsoluto(otro.dia, otro.hora);
    if (Math.abs(inicio - otroInicio) < duracion + descanso) return `No se cumplen los ${descanso} minutos de descanso mínimo.`;
  }
  return null;
}

/**
 * Propone horarios para `partidos`, turno por turno y cancha por cancha.
 *
 * Reglas, en este orden:
 *  1. Una cancha, un partido por turno (respeta lo ya programado en `ocupado`).
 *  2. Una pareja no juega dos partidos en el mismo turno, y un jugador
 *     tampoco aunque esté anotado en dos categorías (se mira por nombre).
 *  3. Un partido va después de todos los que tiene en `despuesDe`.
 *  4. Si se puede, una pareja no juega dos turnos seguidos (descansa uno). Si
 *     no hay otro partido para poner, se pone igual: es preferible a dejar una
 *     cancha vacía.
 *
 * Es una propuesta: el organizador la mira y la confirma, y después puede
 * retocar cualquier partido a mano.
 */
export function repartirHorarios(
  partidos: readonly PartidoARepartir[],
  ocupado: readonly PartidoProgramado[],
  op: OpcionesDeReparto,
): Reparto {
  return repartirHorariosEnDias(partidos, ocupado, { jornadas: [op], cadaMinutos: op.cadaMinutos, canchas: op.canchas });
}

/** Una propuesta cronológica para todas las jornadas, usando fechas reales al cruzar medianoche. */
export function repartirHorariosEnDias(
  partidos: readonly PartidoARepartir[],
  ocupado: readonly PartidoProgramado[],
  op: { jornadas: readonly JornadaDeReparto[]; cadaMinutos: number; canchas: readonly number[] } & CondicionesProgramacion,
): Reparto {
  const asignados: Reparto['asignados'] = [];
  const faltan = [...partidos];
  if (!Number.isFinite(op.cadaMinutos) || op.cadaMinutos <= 0) return { asignados, sinLugar: faltan.map(p => p.id) };
  const turnos = new Set<number>();
  for (const j of op.jornadas) {
    const desde = minutoAbsoluto(j.dia, j.desde);
    const fin = j.hasta === null ? '23:59' : j.hasta;
    const hasta = minutoAbsoluto(j.hasta !== null && fin < j.desde ? diaSiguiente(j.dia) : j.dia, fin);
    for (let t = desde; t <= hasta; t += op.cadaMinutos) turnos.add(t);
  }

  /** Instante real de cada partido, incluso si pertenece a otra jornada. */
  const cuando = new Map<number, number>();
  for (const p of ocupado) {
    if (p.jugado) { cuando.set(p.id, -Infinity); continue; }
    if (!p.dia || !p.hora) continue;
    cuando.set(p.id, minutoAbsoluto(p.dia, p.hora));
  }
  const pendientesDeHorario = new Set([...partidos.map((p) => p.id), ...ocupado.filter(p => !p.jugado && (!p.dia || !p.hora)).map(p => p.id)]);

  const porId = new Map(partidos.map((p) => [p.id, p]));

  /** Quiénes juegan en ese turno (parejas y jugadores), y canchas tomadas: lo ya programado + lo que se va asignando. */
  const enTurno = (t: number) => {
    const parejas = new Set<number>();
    const jugadores = new Set<string>();
    const canchas = new Set<number>();
    for (const p of ocupado) {
      const inicio = p.dia && p.hora ? minutoAbsoluto(p.dia, p.hora) : null;
      if (inicio !== null && inicio < t + op.cadaMinutos && inicio + op.cadaMinutos > t && !p.jugado) {
        p.parejas.forEach((x) => parejas.add(x));
        p.jugadores?.forEach((x) => jugadores.add(x));
        if (p.cancha !== null) canchas.add(p.cancha);
      }
    }
    for (const a of asignados) {
      const inicio = minutoAbsoluto(a.dia, a.hora);
      if (inicio < t + op.cadaMinutos && inicio + op.cadaMinutos > t) {
        canchas.add(a.cancha);
        const p = porId.get(a.id);
        p?.parejas.forEach((x) => parejas.add(x));
        p?.jugadores?.forEach((x) => jugadores.add(x));
      }
    }
    return { parejas, jugadores, canchas };
  };

  for (const t of [...turnos].sort((a, b) => a - b)) {
    if (faltan.length === 0) break;
    const ahora = enTurno(t);
    const antes = enTurno(t - op.cadaMinutos);

    for (const cancha of op.canchas) {
      if (ahora.canchas.has(cancha)) continue;

      const fechaTurno = new Date(t * 60_000).toISOString();
      const otros = [...ocupado, ...asignados.map(a => ({ ...a, parejas: porId.get(a.id)!.parejas, jugadores: porId.get(a.id)!.jugadores, jugado: false }))];
      const puede = (p: PartidoARepartir) =>
        !problemaDeCondiciones(p, fechaTurno.slice(0, 10), fechaTurno.slice(11, 16), otros, op.cadaMinutos, op) &&
        p.parejas.every((x) => !ahora.parejas.has(x)) &&
        (p.jugadores ?? []).every((x) => !ahora.jugadores.has(x)) &&
        p.despuesDe.every((id) => {
          const c = cuando.get(id);
          // Se espera también a los previos sin horario fuera de la selección.
          return c === undefined ? !pendientesDeHorario.has(id) : c + op.cadaMinutos <= t;
        });
      const descansada = (p: PartidoARepartir) =>
        p.parejas.every((x) => !antes.parejas.has(x)) && (p.jugadores ?? []).every((x) => !antes.jugadores.has(x));

      const elegido = faltan.find((p) => puede(p) && descansada(p)) ?? faltan.find(puede);
      if (!elegido) continue;

      faltan.splice(faltan.indexOf(elegido), 1);
      pendientesDeHorario.delete(elegido.id);
      const fecha = new Date(t * 60_000).toISOString();
      asignados.push({ id: elegido.id, dia: fecha.slice(0, 10), hora: fecha.slice(11, 16), cancha });
      cuando.set(elegido.id, t);
      ahora.canchas.add(cancha);
      elegido.parejas.forEach((x) => ahora.parejas.add(x));
      elegido.jugadores?.forEach((x) => ahora.jugadores.add(x));
    }
  }

  return { asignados, sinLugar: faltan.map((p) => p.id) };
}
